import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { ModalShellComponent, ModalShellConfig } from '@shared/components/modal-shell/modal-shell.component';

export interface CommunityTransferDateDialogData {
  initialDate?: string | null;
  minDate?: Date | null;
}

export interface CommunityTransferDateDialogResult {
  validFrom: string;
}

@Component({
  selector: 'app-community-transfer-date-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    DatePickerComponent,
    ModalShellComponent,
  ],
  templateUrl: './community-transfer-date-dialog.component.html',
  styleUrl: './community-transfer-date-dialog.component.scss',
})
export class CommunityTransferDateDialogComponent {
  readonly modalConfig: ModalShellConfig = { title: 'Fecha de inicio del pase', size: 'sm', layout: 'dense' };
  readonly form: FormGroup;
  readonly minDate: any | null;
  readonly maxDate: any;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly dialogRef: MatDialogRef<CommunityTransferDateDialogComponent, CommunityTransferDateDialogResult | null>,
    @Inject(MAT_DIALOG_DATA) public readonly data: CommunityTransferDateDialogData,
  ) {
    this.minDate = data.minDate ? this.fromDate(data.minDate) : null;
    this.maxDate = this.dateAdapter.today();
    this.form = this.fb.group({
      validFrom: [this.fromIsoDate(data.initialDate), [Validators.required, this.notBeforeMinDateValidator(), this.notAfterTodayValidator()]],
    });
  }

  onConfirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close({
      validFrom: this.toIsoDate(this.form.get('validFrom')!.value),
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private fromIsoDate(value?: string | null): any {
    if (!value) {
      return this.dateAdapter.today();
    }

    const [year, month, day] = value.split('-').map(Number);
    return this.dateAdapter.createDate(year, month - 1, day);
  }

  private fromDate(value: Date): any {
    return this.dateAdapter.createDate(value.getFullYear(), value.getMonth(), value.getDate());
  }

  private toIsoDate(value: any): string {
    const year = this.dateAdapter.getYear(value);
    const month = this.dateAdapter.getMonth(value) + 1;
    const day = this.dateAdapter.getDate(value);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private notBeforeMinDateValidator(): (control: AbstractControl) => ValidationErrors | null {
    return control => {
      if (!this.minDate || !control.value) {
        return null;
      }

      return this.dateAdapter.compareDate(control.value, this.minDate) < 0 ? { beforeBirthDate: true } : null;
    };
  }

  private notAfterTodayValidator(): (control: AbstractControl) => ValidationErrors | null {
    return control => !control.value || this.dateAdapter.compareDate(control.value, this.maxDate) <= 0
      ? null
      : { futureDate: true };
  }
}
